import {useRollbar} from '@rollbar/react';
import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {fetchColorsById} from 'hooks/queries/colorsById';
import {fetchStyleById} from 'hooks/queries/styleById';

const ComparisonToolContext = createContext();

export const useComparisonTool = () => {
  const context = useContext(ComparisonToolContext);
  if (!context) {
    throw new Error(
      'useComparisonTool must be used within a ComparisonToolProvider'
    );
  }
  return context;
};

const COMPARE_STYLES_STORAGE_KEY = 'catalog_compare_styles';

const ComparisonToolProvider = ({children, internalCatalog}) => {
  const rollbar = useRollbar();

  // Only render the provider if internalCatalog is true
  if (!internalCatalog) {
    return children;
  }

  const [styleIds, setStyleIds] = useState([]);
  const [isProductComparisonOpen, setIsProductComparisonOpen] = useState(false);
  const [compareStyles, setCompareStyles] = useState([]);
  const [styleLoadingStates, setStyleLoadingStates] = useState({});

  // Load styleIds from localStorage on mount
  useEffect(() => {
    try {
      const savedStyles = localStorage.getItem(COMPARE_STYLES_STORAGE_KEY);
      if (savedStyles) {
        const parsedStorableData = JSON.parse(savedStyles);
        const savedStyleIds = parsedStorableData.map((data) => data.styleId);
        setStyleIds(savedStyleIds);
      }
    } catch (error) {
      rollbar.error('Failed to load compare styles from localStorage:', error);
    }
  }, []);

  // Batch fetch style data when styleIds change
  useEffect(() => {
    const fetchStylesData = async () => {
      if (styleIds.length === 0) {
        setCompareStyles([]);
        setStyleLoadingStates({});
        return;
      }

      // Set loading states for all styles
      const loadingStates = {};
      styleIds.forEach((styleId) => {
        loadingStates[styleId] = true;
      });
      setStyleLoadingStates(loadingStates);

      // Get stored data from localStorage to preserve compareImageUrl
      const getStoredStyleData = (styleId) => {
        try {
          const savedStyles = localStorage.getItem(COMPARE_STYLES_STORAGE_KEY);
          if (savedStyles) {
            const parsedData = JSON.parse(savedStyles);
            const storedItem = parsedData.find(
              (item) => item.styleId === styleId
            );
            return storedItem
              ? {
                  compareImageUrl: storedItem.compareImageUrl,
                  productLink: storedItem.productLink
                }
              : {};
          }
        } catch (error) {
          rollbar.error('Failed to get stored style data:', error);
        }
        return {};
      };

      // Create placeholder styles while loading, including stored data
      const placeholderStyles = styleIds.map((styleId) => {
        const storedData = getStoredStyleData(styleId);
        return {
          id: styleId,
          isPartialData: true,
          loading: true,
          product_id: styleId,
          styleId,
          // Include stored compareImageUrl
          ...storedData
        };
      });
      setCompareStyles(placeholderStyles);

      // Batch fetch all styles with their colors
      const stylePromises = styleIds.map(async (styleId) => {
        try {
          const [styleData, colorsData] = await Promise.all([
            fetchStyleById(styleId),
            fetchColorsById(styleId)
          ]);

          const storedData = getStoredStyleData(styleId);

          return {
            ...styleData,
            colors: colorsData,
            id: styleId,
            isPartialData: false,
            loading: false,
            product_id: styleId,
            sizes: styleData.sizes?.map((size) => size.name) || [],
            styleId,
            // Merge with stored data to preserve compareImageUrl
            ...storedData
          };
        } catch (error) {
          rollbar.error(`Failed to fetch data for style ${styleId}:`, error);
          const storedData = getStoredStyleData(styleId);
          return {
            error,
            hasError: true,
            id: styleId,
            isPartialData: true,
            loading: false,
            product_id: styleId,
            styleId,
            // Include stored compareImageUrl even on error
            ...storedData
          };
        }
      });

      try {
        const resolvedStyles = await Promise.all(stylePromises);
        setCompareStyles(resolvedStyles);

        // Update loading states
        const finalLoadingStates = {};
        resolvedStyles.forEach((style) => {
          finalLoadingStates[style.styleId] = false;
        });
        setStyleLoadingStates(finalLoadingStates);
      } catch (error) {
        rollbar.error('Error fetching styles:', error);
        // Reset loading states on error
        const errorLoadingStates = {};
        styleIds.forEach((styleId) => {
          errorLoadingStates[styleId] = false;
        });
        setStyleLoadingStates(errorLoadingStates);
      }
    };

    fetchStylesData();
  }, [styleIds]);

  const addStyleToCompare = useCallback((style) => {
    const styleId = style.styleId || style.id;

    setStyleIds((prev) => {
      if (prev.includes(styleId)) {
        return prev; // Style already exists
      }
      return [...prev, styleId];
    });

    // Update localStorage with styleId and compareImageUrl
    try {
      const currentStored = localStorage.getItem(COMPARE_STYLES_STORAGE_KEY);
      const currentData = currentStored ? JSON.parse(currentStored) : [];
      const updatedData = [
        ...currentData.filter((item) => item.styleId !== styleId),
        {
          compareImageUrl: style.compareImageUrl,
          productLink: style.productLink,
          styleId
        }
      ];
      localStorage.setItem(
        COMPARE_STYLES_STORAGE_KEY,
        JSON.stringify(updatedData)
      );
    } catch (error) {
      rollbar.error('Failed to update localStorage:', error);
    }
  }, []);

  const removeStyleFromCompare = useCallback((styleId) => {
    setStyleIds((prev) => prev.filter((id) => id !== styleId));

    // Update localStorage to remove the style
    try {
      const currentStored = localStorage.getItem(COMPARE_STYLES_STORAGE_KEY);
      const currentData = currentStored ? JSON.parse(currentStored) : [];
      const updatedData = currentData.filter(
        (item) => item.styleId !== styleId
      );
      localStorage.setItem(
        COMPARE_STYLES_STORAGE_KEY,
        JSON.stringify(updatedData)
      );
    } catch (error) {
      rollbar.error('Failed to update localStorage:', error);
    }
  }, []);

  const clearCompareStyles = useCallback(() => {
    setStyleIds([]);

    // Clear localStorage
    try {
      localStorage.removeItem(COMPARE_STYLES_STORAGE_KEY);
    } catch (error) {
      rollbar.error('Failed to clear localStorage:', error);
    }
  }, []);

  // Refetch specific style data
  const refetchStyleData = useCallback(
    async (styleId) => {
      if (!styleIds.includes(styleId)) {
        return;
      }

      // Set loading state for this specific style
      setStyleLoadingStates((prev) => ({
        ...prev,
        [styleId]: true
      }));

      try {
        const [styleData, colorsData] = await Promise.all([
          fetchStyleById(styleId),
          fetchColorsById(styleId)
        ]);

        const updatedStyle = {
          ...styleData,
          colors: colorsData,
          id: styleId,
          isPartialData: false,
          loading: false,
          product_id: styleId,
          sizes: styleData.sizes?.map((size) => size.name) || [],
          styleId
        };

        // Update the specific style in compareStyles
        setCompareStyles((prev) =>
          prev.map((style) =>
            style.styleId === styleId ? updatedStyle : style
          )
        );

        // Update loading state
        setStyleLoadingStates((prev) => ({
          ...prev,
          [styleId]: false
        }));
      } catch (error) {
        rollbar.error(`Failed to refetch data for style ${styleId}:`, error);

        // Update with error state
        setCompareStyles((prev) =>
          prev.map((style) =>
            style.styleId === styleId
              ? {...style, error, hasError: true, loading: false}
              : style
          )
        );

        setStyleLoadingStates((prev) => ({
          ...prev,
          [styleId]: false
        }));
      }
    },
    [styleIds]
  );

  const contextValue = useMemo(
    () => ({
      addStyleToCompare,
      clearCompareStyles,
      compareStyles,
      isProductComparisonOpen,
      refetchStyleData,
      removeStyleFromCompare,
      setIsProductComparisonOpen,
      styleIds,
      styleLoadingStates
    }),
    [compareStyles, isProductComparisonOpen, styleIds, styleLoadingStates]
  );

  return (
    <ComparisonToolContext.Provider value={contextValue}>
      {children}
    </ComparisonToolContext.Provider>
  );
};

ComparisonToolProvider.propTypes = {
  children: PropTypes.node.isRequired,
  internalCatalog: PropTypes.bool.isRequired
};

export default ComparisonToolProvider;
